import { ApiError } from "@api/errors";
import {
  getGenetechRecordQueryKey,
  useGenetechAddSynonym,
  useGenetechAttach,
  useGenetechChain,
  useGenetechCombine,
  useGenetechCreate,
  useGenetechDetach,
  useGenetechGaps,
  useGenetechLink,
  useGenetechPairs,
  useGenetechRecord,
  useGenetechRegisterList,
  useGenetechRemove,
  useGenetechRemoveSynonym,
  useGenetechRows,
  useGenetechSave,
  useGenetechSynonyms,
  useGenetechUnlink,
} from "@api/generated/genetech/genetech";
import type {
  FieldView,
  Gap,
  GenetechRowsDirection,
  RecordView,
  RegisterViewName as RegisterName,
  RegisterRow,
  RegisterView,
  Related,
} from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { hasRole } from "@app/workspace";
import { AlleleResults } from "@features/genetech/AlleleResults";
import { carriedValues, newValues, type Values } from "@features/genetech/records";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import "@features/genetech/genetech.css";
import { Button, Checkbox, Loader, Select, Tabs, Textarea, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatDate, formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RemovableChip } from "@ui/RemovableChip";
import { SearchBox } from "@ui/SearchBox";
import { SearchSelect } from "@ui/SearchSelect";
import { Splitter } from "@ui/Splitter";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const TABS = ["primers", "combinations", "markers", "genes", "alleles", "gaps", "phenotype"] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute("/lab/genetech")({
  validateSearch: z.object({
    tab: z.enum(TABS).optional(),
    id: z.union([z.number().int(), z.literal("new")]).optional(),
    q: z.string().optional(),
    batch: z.string().optional(), // Gén – fenotípus: the isolation batch and the gene
    gene: z.number().int().optional(),
  }),
  component: GeneTechPage,
});

type Search = z.infer<(typeof Route)["options"]["validateSearch"]>;
type Open = (register: RegisterName, id: number) => void;

// long texts (a text area on the record) and the fields only the record shows
const LONG = new Set(["sequence", "reference", "comment", "function"]);
const RECORD_ONLY = new Set([...LONG, "genome", "labeling", "pos_in_gene"]);
const CELL: Record<FieldView["kind"], string | undefined> = {
  text: "wrap",
  reference: "wrap",
  integer: "mono",
  date: "mono",
  choice: "mono",
};
// the most rows the server gives of a register (``genetech.application.registers.ROWS``)
const ROW_LIMIT = 2000;
const GAP_REGISTER: Record<Gap["kind"], RegisterName> = {
  combination_without_marker: "combinations",
  combination_without_primers: "combinations",
  marker_without_combination: "markers",
  marker_without_allele: "markers",
  allele_without_marker: "alleles",
  gene_without_allele: "genes",
};
// a register's rows and records (``/api/v1/genetech/{register}[/id]``): read again only when that register changed
const RECORDS = /^\/api\/v1\/genetech\/(primers|combinations|markers|genes|alleles)(\/\d+)?$/;

const combName = (id: number) => `C-${String(id).padStart(4, "0")}`;

/** Molekuláris labor → Primerek, markerek, gének (the legacy frmGenTech): the registers, a record with how it hangs
 *  together (a primer's pairs, a gene's synonyms, a marker's chain; every related record opens), and where the chains
 *  break. */
function GeneTechPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = useCallback((p: Partial<Search>) => void navigate({ search: (s) => ({ ...s, ...p }), replace: true }), [navigate]);
  const open: Open = useCallback((register, id) => set({ tab: register, id, q: undefined }), [set]);
  const registers = useGenetechRegisterList().data ?? [];
  const canEdit = hasRole(useUserMe().data?.roles, "labor");
  const tab: Tab = search.tab ?? "markers";
  const spec = registers.find((r) => r.name === tab);
  const client = useQueryClient();
  const [carried, setCarried] = useState<Values>({}); // what a new record takes over from the one on screen
  const startNew = () => {
    const shown =
      spec && typeof search.id === "number" ? client.getQueryData<RecordView>(getGenetechRecordQueryKey(spec.name, search.id)) : undefined;
    setCarried(spec ? carriedValues(spec.name, shown?.values) : {});
    set({ id: "new" });
  };
  return (
    <div className="page fill">
      <PageHeader
        title={t("genetech.title")}
        subtitle={t("genetech.subtitle")}
        actions={
          canEdit &&
          spec && (
            <Button size="compact-sm" onClick={startNew}>
              {t(`genetech.addNew.${spec.name}`)}
            </Button>
          )
        }
      />
      <Tabs value={tab} onChange={(v) => v && set({ tab: v as Tab, id: undefined, q: undefined })}>
        <AnimatedTabsList mb="sm">
          {TABS.map((x) => {
            const count = registers.find((r) => r.name === x)?.count;
            return (
              <Tabs.Tab
                key={x}
                value={x}
                className={x === "gaps" ? "tab-apart" : undefined}
                rightSection={count != null ? <span className="mu sm">{formatNumber(count)}</span> : undefined}
              >
                {t(`genetech.tabs.${x}`)}
              </Tabs.Tab>
            );
          })}
        </AnimatedTabsList>
      </Tabs>
      <div className="fill-row">
        {tab === "gaps" ? (
          <GapsList onOpen={(g) => open(GAP_REGISTER[g.kind], g.id)} />
        ) : tab === "phenotype" ? (
          <AlleleResults batch={search.batch} gene={search.gene} canEdit={canEdit} onChange={set} />
        ) : spec ? (
          <RegisterTable
            key={spec.name}
            spec={spec}
            q={search.q ?? ""}
            selected={search.id}
            onQuery={(q) => set({ q: q || undefined })}
            onOpen={(id) => set({ id })}
          />
        ) : (
          <Loader size="sm" m="md" />
        )}
        {spec && search.id != null && (
          <>
            <Splitter cssVar="--lab-side-w" side="end" min={260} max={640} />
            <aside className="card pad stack lab-side">
              {spec.name === "markers" && search.id !== "new" ? (
                <MarkerPanel
                  key={search.id}
                  id={search.id}
                  spec={spec}
                  canEdit={canEdit}
                  onOpen={open}
                  onClose={() => set({ id: undefined })}
                />
              ) : (
                <RecordPanel
                  key={`${spec.name}-${search.id}`}
                  spec={spec}
                  id={search.id}
                  defaults={search.id === "new" ? newValues(spec.name, carried) : undefined}
                  canEdit={canEdit}
                  onOpen={open}
                  onSaved={(id) => set({ id })}
                  onClose={() => set({ id: undefined })}
                />
              )}
            </aside>
          </>
        )}
      </div>
    </div>
  );
}

function RegisterTable(props: {
  spec: RegisterView;
  q: string;
  selected: number | "new" | undefined;
  onQuery: (q: string) => void;
  onOpen: (id: number) => void;
}) {
  const { t } = useTranslation();
  const { spec } = props;
  const rows = useGenetechRows(spec.name, { q: props.q }, { query: { placeholderData: keepPreviousData } });
  const columns = useMemo<Column<RegisterRow>[]>(
    () => [
      { id: "id", header: "ID", value: (r) => r.id, text: (r) => String(r.id), numeric: true, className: "mono" },
      ...spec.fields
        .filter((f) => !RECORD_ONLY.has(f.name))
        .map(
          (f): Column<RegisterRow> => ({
            id: f.name,
            header: t(`genetech.fields.${f.name}`),
            // a reference by the name it shows, a date by its ISO text (in time order)
            value: (r) => (f.kind === "reference" ? shown(r, f) : r.values[f.name]),
            text: (r) => shown(r, f) ?? "—",
            numeric: f.kind === "integer",
            className: CELL[f.kind],
          }),
        ),
      {
        id: "related",
        header: t(`genetech.related.${spec.name}`),
        value: (r) => r.related.map((x) => x.name ?? x.id).join(", "),
        className: "wrap",
        cell: (r) => (
          <span className="chips">
            {r.related.slice(0, 4).map((x) => (
              <span key={x.id} className="chip">
                {x.name ?? x.id}
              </span>
            ))}
            {r.related.length > 4 && <span className="sm mu">+{r.related.length - 4}</span>}
          </span>
        ),
      },
    ],
    [t, spec],
  );
  // the whole register is here (its first ROW_LIMIT rows at worst): sorted and filtered on the page
  const table = useDataTable(`lab.genetech.${spec.name}`, columns, rows.data, { partial: (rows.data?.length ?? 0) >= ROW_LIMIT });
  return (
    <section className="card stack grow" style={{ gap: 0, minWidth: 0 }}>
      <div className="hstack panel-head" style={{ gap: 8 }}>
        <SearchBox value={props.q} onChange={props.onQuery} placeholder={t("genetech.search")} />
        <span className="grow" />
        {rows.data && <span className="sm mu">{t("genetech.resultCount", { n: rows.data.length })}</span>}
      </div>
      <DataTable
        table={table}
        rowKey={(r) => r.id}
        className="grow"
        rowProps={(r) => ({ className: r.id === props.selected ? "click sel" : "click", onClick: () => props.onOpen(r.id) })}
      />
      {rows.isLoading && <Loader size="sm" m="md" />}
    </section>
  );
}

/** A value as the table shows it: a reference by the name of what it names, a date the Hungarian way. */
function shown(r: RegisterRow, f: FieldView): string | null {
  const v = r.values[f.name];
  const text = v != null ? String(v) : null;
  if (f.kind === "reference") return r.labels[f.name] ?? text;
  return f.kind === "date" && text ? formatDate(text) : text;
}

function problemsOf(error: unknown): Record<string, string> {
  return error instanceof ApiError ? ((error.problem as { fields?: Record<string, string> }).fields ?? {}) : {};
}

/** A write's handlers: afterwards the counts, the gaps and the panels read again, and the rows of the registers it
 *  touched. */
function useLabWrite(touched: RegisterName[], done?: () => void) {
  const client = useQueryClient();
  return {
    onSuccess: () => {
      const paths = touched.map((r) => `/api/v1/genetech/${r}`);
      void client.invalidateQueries({
        predicate: (q) => {
          const key = String(q.queryKey[0]);
          return key.startsWith("/api/v1/genetech/") && (!RECORDS.test(key) || paths.some((p) => key === p || key.startsWith(`${p}/`)));
        },
      });
      done?.();
    },
  };
}

function RecordPanel(props: {
  spec: RegisterView;
  id: number | "new";
  defaults?: Values; // a new record's first values
  canEdit: boolean;
  onOpen: Open;
  onSaved: (id: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { spec, id } = props;
  const found = useGenetechRecord(spec.name, id === "new" ? 0 : id, { query: { enabled: id !== "new" } }).data;
  const [edited, setEdited] = useState<Values>(() => props.defaults ?? {});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [withNull, setWithNull] = useState(false); // a new allele's "<name>Null" allele too (the lab's rule)
  const write = useLabWrite([spec.name]);
  const saved = (message: string) => (out: { id: number }) => {
    notifications.show({ color: "green", message });
    setEdited({});
    setProblems({});
    write.onSuccess();
    props.onSaved(out.id);
  };
  const failed = (e: unknown) => setProblems(problemsOf(e));
  const create = useGenetechCreate({ mutation: { onSuccess: saved(t("genetech.created")), onError: failed } });
  const save = useGenetechSave({ mutation: { onSuccess: saved(t("genetech.saved")), onError: failed } });
  const remove = useGenetechRemove({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("genetech.deleted") });
        write.onSuccess();
        props.onClose();
      },
      onError: () => setConfirming(false),
    },
  });
  if (id !== "new" && !found) return <Loader size="sm" />;
  const values: Values = { ...(found?.values ?? {}), ...edited };
  const title = id === "new" ? t(`genetech.addNew.${spec.name}`) : (found?.name ?? String(id));
  return (
    <div className="stack" style={{ gap: 10 }}>
      <PanelHead title={title} onClose={props.onClose} />
      {spec.fields.map((f) => (
        <FieldInput
          key={f.name}
          // a gene's function, an allele's effect: required when created (older records without them stay valid)
          field={id === "new" && f.required_new ? { ...f, required: true } : f}
          hint={id === "new" && f.required_new ? t("genetech.requiredNew") : undefined}
          value={values[f.name] ?? null}
          label={found?.labels[f.name] ?? null}
          problem={problems[f.name]}
          disabled={!props.canEdit}
          onChange={(v) => setEdited((e) => ({ ...e, [f.name]: v }))}
        />
      ))}
      {props.canEdit && id === "new" && spec.name === "alleles" && (
        <Checkbox
          size="xs"
          label={t("genetech.nullAllele", { name: `${values.allele_name ?? ""}Null` })}
          description={t("genetech.nullAlleleHint")}
          checked={withNull}
          onChange={(e) => setWithNull(e.currentTarget.checked)}
        />
      )}
      {props.canEdit && (
        <div className="hstack" style={{ gap: 8 }}>
          <Button
            size="xs"
            loading={create.isPending || save.isPending}
            disabled={id !== "new" && !Object.keys(edited).length}
            onClick={() =>
              id === "new"
                ? create.mutate({ register: spec.name, data: { values, null_allele: spec.name === "alleles" && withNull } })
                : found && save.mutate({ register: spec.name, id, data: { values, token: found.token } })
            }
          >
            {id === "new" ? t("genetech.create") : t("genetech.save")}
          </Button>
          <span className="grow" />
          {id !== "new" && found && (
            <Button size="xs" variant="default" color="red" onClick={() => setConfirming(true)}>
              {t("genetech.delete")}
            </Button>
          )}
        </div>
      )}
      {confirming && id !== "new" && found && (
        <ConfirmModal
          title={t("genetech.delete")}
          text={t("genetech.deleteConfirm", { name: title })}
          confirm={t("genetech.delete")}
          danger
          loading={remove.isPending}
          onConfirm={() => remove.mutate({ register: spec.name, id, params: { token: found.token } })}
          onClose={() => setConfirming(false)}
        />
      )}
      {id !== "new" && spec.name === "primers" && found && (
        <PrimerPairs primer={id} forward={found.values.direction === "F"} canEdit={props.canEdit} />
      )}
      {id !== "new" && spec.name === "genes" && <Synonyms gene={id} canEdit={props.canEdit} />}
      {found && spec.name !== "primers" && (
        <RelatedList title={t(`genetech.related.${spec.name}`)} items={found.related} onOpen={props.onOpen} />
      )}
    </div>
  );
}

function PanelHead({ title, onClose, extra }: { title: string; onClose: () => void; extra?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="hstack" style={{ gap: 8 }}>
      <h2 className="h2 grow mono">{title}</h2>
      {extra}
      <button type="button" className="chip" aria-label={t("genetech.close")} onClick={onClose}>
        ✕
      </button>
    </div>
  );
}

/** Related records; each opens (the legacy started a chain check from any of them). */
function RelatedList({ title, items, onOpen }: { title: string; items: Related[]; onOpen: Open }) {
  const { t } = useTranslation();
  return (
    <div className="stack" style={{ gap: 4 }}>
      <span className="sec">{title}</span>
      {items.length ? (
        <span className="chips">
          {items.map((x) => (
            <button key={x.id} type="button" className="chip" onClick={() => onOpen(x.register, x.id)}>
              {x.name ?? x.id}
            </button>
          ))}
        </span>
      ) : (
        <span className="sm mu">{t("genetech.none")}</span>
      )}
    </div>
  );
}

function FieldInput(props: {
  field: FieldView;
  value: string | number | null;
  label: string | null;
  problem: string | undefined;
  disabled: boolean;
  hint?: string;
  onChange: (v: string | number | null) => void;
}) {
  const { t } = useTranslation();
  const { field: f } = props;
  const text = props.value != null ? String(props.value) : "";
  const label = t(`genetech.fields.${f.name}`);
  const error = props.problem ? t(`genetech.problems.${props.problem}`) : undefined;
  if (f.references)
    return (
      <RefSelect
        register={f.references}
        label={label}
        required={f.required}
        disabled={props.disabled}
        error={error}
        value={typeof props.value === "number" ? props.value : null}
        current={props.label}
        onChange={props.onChange}
      />
    );
  const common = { size: "xs" as const, label, withAsterisk: f.required, disabled: props.disabled, error, description: props.hint };
  if (f.kind === "choice") return <Select {...common} data={f.choices} value={text || null} onChange={props.onChange} />;
  if (LONG.has(f.name))
    return (
      <Textarea
        {...common}
        autosize
        minRows={1}
        maxRows={5}
        className={f.name === "sequence" ? "mono" : undefined}
        value={text}
        onChange={(e) => props.onChange(e.currentTarget.value)}
      />
    );
  return (
    <TextInput
      {...common}
      type={f.kind === "date" ? "date" : "text"}
      value={text}
      onChange={(e) => props.onChange(e.currentTarget.value)}
    />
  );
}

/** A record of another register, found by typing (its name as the server gives it; 50 hits at most). */
function RefSelect(props: {
  register: RegisterName;
  label: string;
  value: number | null;
  current?: string | null;
  onChange: (v: number | null) => void;
  direction?: GenetechRowsDirection;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) {
  const [q, setQ] = useState("");
  const rows = useGenetechRows(props.register, { q, limit: 50, direction: props.direction }, { query: { enabled: q.length >= 1 } }).data;
  const options = useMemo(() => (rows ?? []).map((r) => ({ value: String(r.id), label: `${r.name ?? r.id} · ${r.id}` })), [rows]);
  return (
    <SearchSelect
      label={props.label}
      required={props.required}
      disabled={props.disabled}
      error={props.error}
      value={props.value}
      current={props.value != null ? `${props.current ?? props.value} · ${props.value}` : null}
      onChange={props.onChange}
      onQuery={setQ}
      options={options}
    />
  );
}

function PrimerPairs({ primer, forward, canEdit }: { primer: number; forward: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const pairs = useGenetechPairs(primer).data ?? [];
  const [other, setOther] = useState<number | null>(null);
  const mutation = useLabWrite(["primers", "combinations"], () => setOther(null));
  const attach = useGenetechAttach({ mutation });
  const detach = useGenetechDetach({ mutation });
  const combine = useGenetechCombine({ mutation });
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="sec">{t("genetech.primerPairs")}</span>
      {pairs.map((p) => (
        <div key={p.pair_id} className="hstack sm" style={{ gap: 6 }}>
          <span className="mono grow">
            {p.forward?.name ?? "—"} + {p.reverse?.name ?? "—"}
          </span>
          {p.comb_id != null ? (
            <span className="chip g">{combName(p.comb_id)}</span>
          ) : (
            canEdit && (
              <Button size="compact-xs" variant="default" onClick={() => combine.mutate({ pairId: p.pair_id, data: {} })}>
                {t("genetech.createCombination")}
              </Button>
            )
          )}
          {canEdit && (
            <button type="button" className="chip" aria-label={t("genetech.unpair")} onClick={() => detach.mutate({ pairId: p.pair_id })}>
              ✕
            </button>
          )}
        </div>
      ))}
      {!pairs.length && <span className="sm mu">{t("genetech.none")}</span>}
      {canEdit && (
        <div className="hstack" style={{ gap: 6, alignItems: "flex-end" }}>
          <RefSelect
            label={forward ? t("genetech.reversePrimer") : t("genetech.forwardPrimer")}
            register="primers"
            direction={forward ? "R" : "F"}
            value={other}
            onChange={setOther}
          />
          <Button
            size="xs"
            disabled={other == null}
            loading={attach.isPending}
            onClick={() =>
              other != null && attach.mutate({ data: forward ? { forward: primer, reverse: other } : { forward: other, reverse: primer } })
            }
          >
            {t("genetech.pair")}
          </Button>
        </div>
      )}
    </div>
  );
}

function Synonyms({ gene, canEdit }: { gene: number; canEdit: boolean }) {
  const { t } = useTranslation();
  const synonyms = useGenetechSynonyms(gene).data ?? [];
  const [text, setText] = useState("");
  const mutation = useLabWrite(["genes"], () => setText(""));
  const add = useGenetechAddSynonym({ mutation });
  const remove = useGenetechRemoveSynonym({ mutation });
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="sec">{t("genetech.synonyms")}</span>
      <span className="chips">
        {synonyms.map((s) =>
          canEdit ? (
            <RemovableChip
              key={s}
              removeLabel={t("genetech.removeSynonym", { s })}
              onRemove={() => remove.mutate({ gene, params: { synonym: s } })}
            >
              {s}
            </RemovableChip>
          ) : (
            <span key={s} className="chip">
              {s}
            </span>
          ),
        )}
        {!synonyms.length && <span className="sm mu">{t("genetech.none")}</span>}
      </span>
      {canEdit && (
        <div className="hstack" style={{ gap: 6 }}>
          <TextInput
            size="xs"
            className="grow"
            aria-label={t("genetech.newSynonym")}
            placeholder={t("genetech.newSynonym")}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
          />
          <Button size="xs" disabled={!text.trim()} loading={add.isPending} onClick={() => add.mutate({ gene, data: { synonym: text } })}>
            {t("genetech.add")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A marker's chain as the design draws it: primers → combination → marker → genes → alleles, where it breaks, its
 *  links made and taken apart here; the combination, a gene and an allele open. */
function MarkerPanel(props: { id: number; spec: RegisterView; canEdit: boolean; onOpen: Open; onClose: () => void }) {
  const { t } = useTranslation();
  const { id, canEdit } = props;
  const [editing, setEditing] = useState(false);
  const chain = useGenetechChain(id).data;
  const [allele, setAllele] = useState<number | null>(null);
  const [gene, setGene] = useState<number | null>(null);
  const mutation = useLabWrite(["markers", "alleles", "genes"], () => {
    setAllele(null);
    setGene(null);
  });
  const link = useGenetechLink({ mutation });
  const unlink = useGenetechUnlink({ mutation });
  if (editing)
    return (
      <RecordPanel
        spec={props.spec}
        id={id}
        canEdit={canEdit}
        onOpen={props.onOpen}
        onSaved={() => setEditing(false)}
        onClose={() => setEditing(false)}
      />
    );
  if (!chain) return <Loader size="sm" />;
  const m = chain.marker;
  const comb = chain.combination;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <PanelHead
        title={t("genetech.linksTitle", { name: m.marker_name ?? id })}
        onClose={props.onClose}
        extra={
          canEdit && (
            <button type="button" className="gb-bare sm link" onClick={() => setEditing(true)}>
              {t("genetech.edit")}
            </button>
          )
        }
      />
      <div className="lab-pair">
        <Box kind={t("genetech.primerF")} title={comb?.forward?.name} sub={comb?.forward?.sequence} mono />
        <Box kind={t("genetech.primerR")} title={comb?.reverse?.name} sub={comb?.reverse?.sequence} mono />
      </div>
      <span className="lab-arrow">↓</span>
      <Box
        kind={t("genetech.primerCombination")}
        title={comb ? combName(comb.id) : null}
        sub={comb?.annealing != null ? t("genetech.annealing", { degrees: comb.annealing }) : comb?.comment}
        onOpen={comb ? () => props.onOpen("combinations", comb.id) : undefined}
      />
      <span className="lab-arrow">↓</span>
      <Box
        kind={t("genetech.marker")}
        title={[m.marker_name, m.type, [m.chrom, m.chrom_arm].filter(Boolean).join(" / ")].filter(Boolean).join(" · ")}
        sub={m.product != null ? String(m.product) : null}
        on
      />
      <span className="lab-arrow">↓</span>
      <div className="lab-pair">
        {chain.genes.map((g) => (
          <Box
            key={g.id ?? "none"}
            kind={t("genetech.gene")}
            title={g.name}
            sub={g.function}
            onOpen={g.id != null ? () => props.onOpen("genes", g.id as number) : undefined}
          />
        ))}
        {!chain.genes.length && <Box kind={t("genetech.gene")} title={null} />}
      </div>
      <div className="lab-box">
        <span className="sm mu">{t("genetech.alleles")}</span>
        {chain.genes.flatMap((g) =>
          g.alleles.map((a) => (
            <div key={a.id} className="hstack sm" style={{ gap: 6 }}>
              <button type="button" className="gb-bare grow" style={{ textAlign: "left" }} onClick={() => props.onOpen("alleles", a.id)}>
                <b>{a.name}</b>
                {a.effect != null && <span className="mu"> ({a.effect})</span>}
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="chip"
                  aria-label={t("genetech.detach", { name: a.name })}
                  onClick={() => unlink.mutate({ params: { marker: id, allele: a.id } })}
                >
                  ✕
                </button>
              )}
            </div>
          )),
        )}
        {!chain.genes.length && <span className="sm mu">—</span>}
      </div>
      {chain.breaks.map((b) => (
        <p key={b} className="note warn sm">
          {t(`genetech.chainBreaks.${b}`)}
        </p>
      ))}
      {canEdit && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="sec">{t("genetech.createLink")}</span>
          <RefSelect
            label={t("genetech.allele")}
            register="alleles"
            value={allele}
            onChange={(v) => {
              setAllele(v);
              setGene(null);
            }}
          />
          <RefSelect
            label={t("genetech.orGene")}
            register="genes"
            value={gene}
            onChange={(v) => {
              setGene(v);
              setAllele(null);
            }}
          />
          <Button
            size="xs"
            disabled={allele == null && gene == null}
            loading={link.isPending}
            onClick={() => link.mutate({ data: { marker: id, allele, gene } })}
          >
            {t("genetech.link")}
          </Button>
        </div>
      )}
      <p className="sm mu">{t("genetech.sampleResults", { n: chain.results })}</p>
    </div>
  );
}

function Box(props: { kind: string; title?: string | null; sub?: string | null; mono?: boolean; on?: boolean; onOpen?: () => void }) {
  const body = (
    <>
      <span className="sm mu">{props.kind}</span>
      <b className={props.mono ? "mono" : undefined}>{props.title ?? "—"}</b>
      {props.sub && <span className={props.mono ? "sm mono mu" : "sm mu"}>{props.sub}</span>}
    </>
  );
  const className = props.on ? "lab-box on" : "lab-box";
  return props.onOpen ? (
    <button type="button" className={className} onClick={props.onOpen}>
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}

function GapsList({ onOpen }: { onOpen: (g: Gap) => void }) {
  const { t } = useTranslation();
  const gaps = useGenetechGaps().data;
  if (!gaps) return <Loader size="sm" m="md" />;
  const byKind = new Map<Gap["kind"], Gap[]>();
  for (const g of gaps) byKind.set(g.kind, [...(byKind.get(g.kind) ?? []), g]);
  return (
    <section className="card pad stack grow scroll" style={{ gap: 14 }}>
      <p className="sm mu">{t("genetech.gapsHelp")}</p>
      {[...byKind].map(([kind, list]) => (
        <div key={kind} className="stack" style={{ gap: 6 }}>
          <span className="sec">
            {t(`genetech.gaps.${kind}`)} · {list.length}
          </span>
          <span className="chips">
            {list.map((g) => (
              <button key={`${g.kind}-${g.id}`} type="button" className="chip" onClick={() => onOpen(g)}>
                {g.name ?? g.id}
              </button>
            ))}
          </span>
        </div>
      ))}
      {!gaps.length && <p className="note ok sm">{t("genetech.noGaps")}</p>}
    </section>
  );
}
